import type { NormalizedSwatchCrudPayload } from "@/util/colorPicker";
import { getColorPickerSwatchValue } from "@/util/colorPicker";
import { defaultColorsArray, GlobalSettingsEvents } from "@/util/constants";
import { computed, type ComputedRef } from "vue";

export interface NormalizedColorSwatch {
  value: string;
  value8: string;
  alpha: number;
  label: string;
  labelValue: string;
}

interface SwatchSection {
  key: string;
  label: string;
  swatches: NormalizedColorSwatch[];
  showAddButton: boolean;
  editable: boolean;
  showTooltip: "name" | "value" | "both" | false;
  showTransparentSwatch?: boolean;
}

interface ColorPickerSwatchesStore {
  customColors?: unknown[];
  defaultColors?: unknown[];
  brandColors?: unknown[];
  appHandshake?: Promise<{ emit: (event: string) => void }> | null;
}

interface UseColorPickerSwatchesOptions {
  t: (key: string) => string;
  onApplyColor: (value: string) => void;
  /** When true, renders the built-in "transparent" swatch in the default section. */
  showTransparent?: boolean;
}

const normalizeSwatchEntry = ({
  value = "",
  value8 = "",
  alpha = 1,
  label = "",
  labelValue,
}: Partial<NormalizedColorSwatch> &
  Record<string, unknown>): NormalizedColorSwatch => ({
  value: String(value),
  value8: String(value8),
  alpha: Number(alpha),
  label: String(label),
  labelValue: String(labelValue ?? label ?? ""),
});

/**
 * @description Provides reactive `swatches` sections for HLColorPicker,
 * plus shared handlers for swatch-add and default-swatch-add actions.
 * Eliminates duplicated swatch section building across color picker components.
 *
 * @param store - Store with customColors, defaultColors, brandColors, and appHandshake.
 * @param options - i18n `t` function and `onApplyColor` callback invoked on swatch selection.
 * @returns Reactive swatches sections, onSwatchAdd handler, and handleDefaultSwatchAdd handler.
 */
export const useColorPickerSwatches = (
  store: ColorPickerSwatchesStore,
  { t, onApplyColor, showTransparent = false }: UseColorPickerSwatchesOptions
) => {
  const defaultSwatches = computed(() =>
    Array.isArray(store.defaultColors) && store.defaultColors.length
      ? store.defaultColors
      : defaultColorsArray
  );

  const swatches: ComputedRef<SwatchSection[]> = computed(() => {
    const customSwatches = Array.isArray(store.customColors)
      ? (
          store.customColors as Array<
            Partial<NormalizedColorSwatch> & Record<string, unknown>
          >
        ).map(normalizeSwatchEntry)
      : [];

    const sections: SwatchSection[] = [
      ...(customSwatches.length
        ? [
            {
              key: "custom",
              label: t("common.colorPicker.globalCustomColors"),
              swatches: customSwatches,
              showAddButton: true,
              showTooltip: "name" as const,
              editable: false,
            },
          ]
        : []),
      {
        key: "default",
        label: t("common.colorPicker.defaultColors"),
        swatches: defaultSwatches.value as NormalizedColorSwatch[],
        showAddButton: true,
        editable: true,
        showTooltip: "both" as const,
        showTransparentSwatch: showTransparent,
      },
    ];

    const hasBrandColors =
      Array.isArray(store.brandColors) && store.brandColors.length > 0;
    if (hasBrandColors) {
      const brandSwatches = (
        store.brandColors as Array<
          Partial<NormalizedColorSwatch> & Record<string, unknown>
        >
      ).map(normalizeSwatchEntry);
      sections.unshift({
        key: "brand",
        label: t("common.colorPicker.brandColors"),
        swatches: brandSwatches,
        showAddButton: false,
        editable: false,
        showTooltip: "both" as const,
      });
    }

    return sections;
  });

  const onSwatchAdd = () => {
    store.appHandshake?.then((parent) => {
      parent.emit(GlobalSettingsEvents.OPEN_GLOBAL_SETTINGS);
    });
  };

  const handleDefaultSwatchAdd = (payload: NormalizedSwatchCrudPayload) => {
    const swatchValue = getColorPickerSwatchValue(payload);
    if (!swatchValue) {
      return;
    }
    onApplyColor(swatchValue);
  };

  return { swatches, onSwatchAdd, handleDefaultSwatchAdd } as const;
};
